import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { tabsManager, TabLifecycleConfig } from '../components/layout/TabsManager';

// 存储工具函数
const storage = {
  setItem: (key: string, value: any) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      console.log('💾 [SessionStorage] 存储成功:', key, value);
    } catch (error) {
      console.error('❌ [SessionStorage] 存储失败:', error);
    }
  },
  
  getItem: (key: string) => {
    try {
      const item = sessionStorage.getItem(key);
      const result = item ? JSON.parse(item) : null;
      console.log('📖 [SessionStorage] 读取:', key, result ? '成功' : '不存在');
      return result;
    } catch (error) {
      console.error('❌ [SessionStorage] 读取失败:', error);
      return null;
    }
  },
  
  removeItem: (key: string) => {
    try {
      sessionStorage.removeItem(key);
      console.log('🗑️ [SessionStorage] 删除成功:', key);
    } catch (error) {
      console.error('❌ [SessionStorage] 删除失败:', error);
    }
  }
};

/**
 * 标签页状态管理Hook
 * 提供统一的接口来管理页面状态和生命周期
 */
export const useTabStateManagement = (config: TabLifecycleConfig) => {
  const location = useLocation();
  const tabIdRef = useRef<string>('');
  const isInitialized = useRef(false);

  // 生成稳定的标签页ID
  const generateTabId = useCallback((path: string, config: TabLifecycleConfig): string => {
    // 移除动态参数，生成稳定的ID
    const stablePath = path.replace(/\/\d+/g, '/:id');
    
    // 如果有自定义存储键，使用它来区分不同的配置
    if (config.customStorageKey) {
      return `${stablePath}_${config.customStorageKey}`;
    }
    
    return stablePath;
  }, []);

  // 获取当前标签页ID
  const getCurrentTabId = useCallback((): string => {
    if (!tabIdRef.current) {
      tabIdRef.current = generateTabId(location.pathname, config);
    }
    return tabIdRef.current;
  }, [location.pathname, generateTabId, config]);

  // 保存状态
  const saveState = useCallback((state: any) => {
    const tabId = getCurrentTabId();
    
    // 确保配置已注册
    if (!tabsManager.hasLifecycleConfig(tabId)) {
      tabsManager.registerLifecycleHandler(tabId, config);
    }
    
    tabsManager.saveTabState(tabId, state);
  }, [getCurrentTabId, config]);

  // 恢复状态
  const restoreState = useCallback(() => {
    const tabId = getCurrentTabId();
    
    // 确保配置已注册
    if (!tabsManager.hasLifecycleConfig(tabId)) {
      tabsManager.registerLifecycleHandler(tabId, config);
    }
    
    return tabsManager.restoreTabState(tabId);
  }, [getCurrentTabId, config]);

  // 清理状态
  const clearState = useCallback(() => {
    const tabId = getCurrentTabId();
    
    // 确保配置已注册
    if (!tabsManager.hasLifecycleConfig(tabId)) {
      // 配置未注册，重新注册
    }
    
    tabsManager.clearTabState(tabId);
  }, [getCurrentTabId, config]);

  // 注册生命周期配置
  useEffect(() => {
    if (!isInitialized.current) {
      const tabId = getCurrentTabId();
      
      // 注册生命周期配置
      tabsManager.registerLifecycleHandler(tabId, config);
      
      isInitialized.current = true;
      
      // 清理函数：注销生命周期配置
      return () => {
        tabsManager.unregisterLifecycleHandler(tabId);
      };
    }
  }, [getCurrentTabId, config]); // 添加config依赖，确保配置变化时能重新注册

  // 当路径变化时，更新标签页ID
  useEffect(() => {
    const newTabId = generateTabId(location.pathname, config);
    if (newTabId !== tabIdRef.current) {
      // 路径变化，更新标签页ID
      const oldTabId = tabIdRef.current;
      if (oldTabId && isInitialized.current) {
        // 注销旧标签页的生命周期配置
        tabsManager.unregisterLifecycleHandler(oldTabId);
      }
      
      tabIdRef.current = newTabId;
      isInitialized.current = false;
    }
  }, [location.pathname, config]); // 添加config依赖，确保路径变化时能正确生成新ID

  return {
    saveState,
    restoreState,
    clearState,
    getCurrentTabId,
    // 导出配置信息，方便调试
    config
  };
};

/**
 * 简化的标签页状态管理Hook
 * 适用于只需要基本状态管理的页面
 */
export const useSimpleTabState = (keepState: boolean = false, persistToStorage: boolean = false) => {
  // 为普通状态管理使用不同的标签页ID，避免与持久化状态冲突
  const simpleConfig = {
    keepState,
    persistToStorage: persistToStorage, // 允许传入persistToStorage参数
    customStorageKey: 'simple_tab_state', // 使用不同的存储键
    onTabActivate: () => {
      // 普通标签页激活
    },
    onTabDeactivate: () => {
      // 普通标签页失活
    },
    onTabClose: () => {
      // 普通标签页关闭
    }
  };
  
  // useSimpleTabState 配置已设置
  
  return useTabStateManagement(simpleConfig);
};

/**
 * 持久化标签页状态管理Hook
 * 适用于需要持久化状态的页面
 */
export const usePersistentTabState = (
  customStorageKey?: string,
  keepState: boolean = true
) => {
  // 为持久化状态管理使用明确的标签页ID
  const persistentConfig = {
    keepState,
    persistToStorage: true,
    customStorageKey: customStorageKey || 'persistent_tab_state', // 默认使用persistent前缀
    onTabActivate: () => {
      // 持久化标签页激活
    },
    onTabDeactivate: () => {
      // 持久化标签页失活
    },
    onTabClose: () => {
      // 持久化标签页关闭
    },
    // 添加自动保存逻辑
    onSaveState: () => {
      // 持久化标签页自动保存状态
      return null; // 返回null，让Hook使用传入的state
    }
  };
  
  // usePersistentTabState 配置已设置
  
  return useTabStateManagement(persistentConfig);
};
